import type { ColorToken, IconName } from "@bundle/tokens";
import type { Ref } from "react";
import { usePrimitiveT } from "../i18n/I18nProvider.tsx";
import { Icon } from "./Icon.tsx";
import { cx } from "./tokens.ts";
import { Tooltip } from "./Tooltip.tsx";

export type IconButtonProps = {
  icon: IconName;
  onPress: () => void;
  /** The button's name (also its tooltip). A badge is appended for assistive tech: "Inbox, 3 unread". */
  accessibilityLabel: string;
  variant?: "ghost" | "glass" | "filled";
  size?: "sm" | "md" | "lg";
  /** Count (or true for a dot) shown on the corner. */
  badge?: number | boolean;
  /** The icon's colour (default: the button's text colour). */
  color?: ColorToken;
  disabled?: boolean;
  testID: string;
};

const iconSize = { sm: "icon.sm", md: "icon.sm", lg: "icon.md" } as const;

export function IconButton(p: IconButtonProps & { ref?: Ref<HTMLButtonElement> } & Record<string, unknown>) {
  const { icon, onPress, accessibilityLabel, variant = "ghost", size = "md", badge, color, disabled, testID, ref, ...rest } = p;
  const t = usePrimitiveT();
  const hasBadge = badge !== undefined && badge !== false && badge !== 0;
  const name = !hasBadge
    ? accessibilityLabel
    : badge === true
      ? t("primitive.iconButton.badgeDot", { label: accessibilityLabel })
      : t("primitive.iconButton.badgeCount", { label: accessibilityLabel, count: badge });
  const button = (
    <button
      {...rest}
      ref={ref}
      type="button"
      aria-label={name}
      data-testid={testID}
      disabled={disabled}
      className={cx("b-icon-button", `b-icon-button-${variant}`, `b-icon-button-${size}`)}
      onClick={(e) => {
        e.stopPropagation();
        onPress();
        const onClick = rest.onClick;
        if (typeof onClick === "function") onClick(e);
      }}
    >
      <Icon name={icon} size={iconSize[size]} {...(color === undefined ? {} : { color })} />
      {!hasBadge ? null : (
        <span className={badge === true ? "b-icon-button-dot" : "b-icon-button-badge"} aria-hidden>
          {badge === true ? "" : badge > 99 ? "99+" : String(badge)}
        </span>
      )}
    </button>
  );
  return <Tooltip label={accessibilityLabel}>{button}</Tooltip>;
}
